import { Drone, ID, Permissions, Viewer } from "@nova/constants"
import { ProjectExtended, WorkspaceMember } from "@nova/schemas"
import { DiscriminatedUnion } from "@nova/util-ts"
import { WorkspaceTreeItem } from "features/data-management/workspace/WorkspaceManagementSlice"

export type TableMember = DiscriminatedUnion<WorkspaceMember> & {
    access: Permissions
    pluginOrder: string
    licenseOrder: number
}

export enum WorkspaceEntityType {
    Folder = "folder",
    Project = "project",
}

export type WorkspaceFoldersAndProjectsProps = {
    allFoldersById: Record<string, Folder>
    allProjectsById: Record<string, Project>
    activeFolder: WorkspaceTreeItem | undefined
    createTagChain: (id: ID) => string[]
    bodyFolderIds: string[]
    bodyProjectIds: string[]
    sortedSidePanelRootFolders: Folder[]
    sortedFavoriteIds: ID[]
    sortedFavoriteFolderIds: string[]
    sortedFavoriteProjectIds: string[]
    isFavorite: (id: ID) => boolean
    handleToggleFavorite: (id: ID) => Promise<string[]>
    handleOverwriteFavorites: (favoriteIds: ID[]) => void
    sortedRecentProjectIds: string[]
    handleGoToRoot: () => void
    handleActivateProject: (
        projectId: ID,
        options?: { skipNavigationCheck?: boolean; skipEntityExistenceCheck?: boolean }
    ) => Promise<void>
    handleMove: (id: ID, type: WorkspaceEntityType, newParentFolderId?: ID) => void
    handleCreateFolder: (insideOfId?: ID) => Promise<void>
    handleCreateProject: () => Promise<void>
    handleDelete: (id: ID, name: string, type: WorkspaceEntityType) => void
    handleBeginRenaming: (key: ID) => {
        payload: string | undefined
        type: string
    }
    handleConfirmRenaming: (id: ID, name: string, type: WorkspaceEntityType) => void
    handleCancelRenaming: () => {
        payload: string | undefined
        type: string
    }
    handleOpenShareModal: (id: ID) => void
    countChildren: (folder: Folder) => {
        numberOfSubFolders: number
        numberOfSubProjects: number
        mostRecentChildUpdatedAt: string
        viewers: Viewer[]
        drones: Drone[]
    }
}

export type WorkspaceFolderProps = {
    folder: Folder
    tagChain: ID[]
    numberOfSubFolders: number
    numberOfSubProjects: number
    mostRecentChildUpdatedAt: string
    isFavorite: boolean
    viewers: Viewer[]
    drones: Drone[]
} & Pick<
    WorkspaceFoldersAndProjectsProps,
    | "handleMove"
    | "handleCreateFolder"
    | "handleBeginRenaming"
    | "handleConfirmRenaming"
    | "handleCancelRenaming"
    | "handleOpenShareModal"
    | "handleToggleFavorite"
    | "handleDelete"
>

export type WorkspaceProjectProps = {
    project: Project
    isFavorite: boolean
} & Pick<
    WorkspaceFoldersAndProjectsProps,
    | "handleActivateProject"
    | "handleMove"
    | "handleBeginRenaming"
    | "handleConfirmRenaming"
    | "handleCancelRenaming"
    | "handleOpenShareModal"
    | "handleToggleFavorite"
    | "handleDelete"
>

export type WorkspaceFolderTabProps = Omit<
    WorkspaceFolderProps,
    | "isFavorite"
    | "numberOfSubFolders"
    | "numberOfSubProjects"
    | "mostRecentChildUpdatedAt"
    | "viewers"
    | "drones"
    | "handleActivateProject"
> &
    Pick<WorkspaceFoldersAndProjectsProps, "isFavorite"> & {
        categoryKey: string
        subFolders?: WorkspaceTreeItem[]
        shouldPopulateChildren: boolean
        depth: number
        extraCanDropCondition?: ({ id, type }: { id: ID; type: WorkspaceEntityType }) => boolean
    }

export type WorkspaceProjectTabProps = WorkspaceProjectProps & { categoryKey: string }

export type Folder = WorkspaceTreeItem & {
    createdAt?: string
    updatedAt?: string
}

export type Project = ProjectExtended
